import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRoutingService } from './http-routing.service';
import { Observable } from 'rxjs';
import { PartnerRegistrationInput } from '@zp-shared/models/auth.model';

import { HeaderService } from './header.service';
import { AuthResponse, UserInfo } from '@zp-shared/models/auth.mode';
/**
 * Service for handling authentication-related operations.
 * This service provides methods for managing user sessions, such as logging out.
 * Author: Maharajan (CEN349)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /**
   * Signal to store user data
   */
  userData: WritableSignal<UserInfo | null> = signal(null);
  /**
   * Signal to store user token
   */
  userToken: WritableSignal<string | null> = signal(null);
  /**
   * Variable user to send Data between Two Browser Tabs
   */
  broadcastAuth = new BroadcastChannel('authenticate');

  /**
   * Constructor for AuthService.
   * @param router The Angular Router service for navigation.
   * @param httpService The HTTP routing service for making API requests.
   * @param headerService The header service for setting request headers.
   */
  constructor(
    private router: Router,
    private httpService: HttpRoutingService,
    private headerService: HeaderService
  ) { }

  /**
   * Method to retrieve the JWT token from local storage.
   * @returns The JWT token as a string, or null if not found.
   */
  getJwtToken(): string | null {
    return localStorage.getItem('jwt_token');
  }
  /**
   * Method used to check if a JWT token is expired.
   * @param token to be checked
   * @returns boolean indicating whether the token is expired or not.
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp;
      if (!expiry) return false;
      return Date.now() >= expiry * 1000;
    } catch {
      return true;
    }
  }
  /**
   * Logs out the current user.
   * Removes the JWT token from local storage and navigates the user to the sign-in page.
   */
  logout() {
    localStorage.removeItem('jwt_token');
    this.broadcastAuth.postMessage('logout');
    this.router.navigate(['/signin']);
  }
  /**
   * Method to get list of countries.
   * @param url The API endpoint URL.
   * @returns Observable of the HTTP response.
   */
  getCountries(url: string): Observable<any> {
    return this.httpService.getMethod(url);
  }
  /**
   * Method to get list of states for a country.
   * @param url The API endpoint URL.
   * @param data The query parameters (e.g., countryId).
   * @returns Observable of the HTTP response.
   */
  getStates(url: string, data: { countryId: number }): Observable<any> {
    return this.httpService.getMethod(url, data);
  }
  /**
   * Method to create a new partner.
   * @param url The API endpoint URL.
   * @param data The partner registration data.
   * @returns Observable of the HTTP response.
   */
  createUser(url: string, data: Partial<PartnerRegistrationInput>): Observable<any> {
    return this.httpService.postMethod(url, data)
  }
  /**
   * Method used to check whether the user is authenticated or not.
   * @returns {boolean}
   */
  isAuthenticated(): boolean {
    return !!this.getJwtToken();
  }
  /**
   * Resets the user's password.
   * @param password The new password to be set.
   * @returns An Observable of the authentication response.
   */
  resetPassword(password: string): Observable<any> {
    const userEmail = this.userData()?.email;
    console.log(userEmail);
    const encodeinfo = userEmail + ':' + password;
    const encoded = btoa(encodeinfo);
    const token = this.userToken();
    this.headerService.setHeader('default', 'registration', encoded);
    return this.httpService.putMethod('/v1/auth/reset-password', {
      token,
    }) as Observable<AuthResponse>;
  }
  /**
   * Verifies the token.
   * @param token The token to be verified.
   * @returns An Observable of the authentication response.
   */
  verifytoken(token: string) {
    return this.httpService.postMethod('/v1/auth/verify-token', {
      token,
    }) as Observable<any>;
  }
  /**
   * Calls the backend API to validate if the provided email is associated
   * with any existing user. This is a key step in the forgot-password flow.
   *
   * @param email The email address to validate.
   * @returns An observable containing the backend response.
   */
  validateEmail(email: string): Observable<any> {
    console.log('email', email);
    return this.httpService.postMethod('/v1/auth/forgot-password', {
      email: email,
    });
  }
}
